import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './HomePage.scss';
import { CircularProgress, Alert } from '@mui/material';
import { HiOutlineBadgeCheck } from 'react-icons/hi';
import { mapPlanIdToPriceKey, NEW_PRICING_BASE_PRICES } from '../../constants';

interface ApiPlan {
  id: number;
  title: string;
  plan_option_id: number;
}

interface PlanOption {
  title: string;
  order: number;
}

interface PlanOptionsResponse {
  data: { [key: string]: PlanOption };
}

interface DisplayPlan {
  id: number;
  name: string;
  features: string[];
}

interface PlansPageProps {
  isAuthenticated: boolean;
  theme: string;
}

const API_BASE = 'https://api.neontradepro.com';

const PlansPage: React.FC<PlansPageProps> = ({ isAuthenticated, theme }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<DisplayPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [accountValue, setAccountValue] = useState<string>('');
  const [calculatedPrices, setCalculatedPrices] = useState<{ [key: number]: number | null }>({});
  const [calculatorErrorMessage, setCalculatorErrorMessage] = useState<string | null>(null);

  const apiFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('token');
    const isFormData = options.body instanceof FormData;
    const headers: HeadersInit = { ...options.headers } as HeadersInit;

    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (!isFormData) headers['Content-Type'] = 'application/json';

    const response = await fetch(`${API_BASE}${url}`, { ...options, headers });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'An unknown error occurred' }));
      throw new Error(errorData.detail || 'Network response was not ok');
    }
    return response.json();
  };

  useEffect(() => {
    const fetchPlansAndFeatures = async () => {
      setLoading(true);
      setError(null);
      try {
        const apiPlans: ApiPlan[] = await apiFetch('/plan/');

        const plansWithFeatures = await Promise.all(
          apiPlans.map(async (plan) => {
            let features: string[] = [];
            if (plan.plan_option_id) {
              try {
                const options: PlanOptionsResponse = await apiFetch(`/plan/options/${plan.plan_option_id}`);
                if (options && options.data) {
                  features = Object.values(options.data)
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map(option => option.title);
                }
              } catch (e: any) {
                console.error(`Could not fetch features for plan ${plan.id}:`, e.message || e);
              }
            }
            return {
              id: plan.id,
              name: plan.title,
              features: features,
            };
          })
        );
        setPlans(plansWithFeatures);
      } catch (err: any) {
        console.error('Failed to fetch plans:', err);
        setError(`خطا در بارگذاری پلن‌ها: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchPlansAndFeatures();
  }, []);

  const calculatePriceForPlanInternal = useCallback((planKey: string, account: number): number | null => {
    if (!planKey || isNaN(account) || account < 0) return null;
    const basePrice = NEW_PRICING_BASE_PRICES[planKey];
    if (basePrice === undefined) return null;
    const coefficients: { [key: string]: number } = { a: 0.0003, b: 0.0005, c: 0.0007 };
    const coefficient = coefficients[planKey];
    if (coefficient === undefined) return null;
    const rawCalculatedPrice = account * coefficient + basePrice;
    return Math.round(rawCalculatedPrice);
  }, []);

  const handleAccountValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAccountValue(value);
    if (Object.keys(calculatedPrices).length > 0) setCalculatedPrices({});
    if (calculatorErrorMessage) setCalculatorErrorMessage(null);
  };

  const handleCalculatePrices = () => {
    setCalculatorErrorMessage(null);
    const accountNum = parseFloat(accountValue);

    if (accountValue.trim() === '') {
      setCalculatedPrices({});
      setCalculatorErrorMessage('لطفاً مقداری را وارد کنید.');
      return;
    }

    if (isNaN(accountNum) || accountNum < 0) {
      setCalculatedPrices({});
      setCalculatorErrorMessage('لطفاً یک عدد معتبر و مثبت وارد کنید.');
      return;
    }

    const newCalculatedPrices: { [key: number]: number | null } = {};
    plans.forEach(plan => {
      const planKey = mapPlanIdToPriceKey(plan.id);
      if (planKey) {
        newCalculatedPrices[plan.id] = calculatePriceForPlanInternal(planKey, accountNum);
      } else {
        newCalculatedPrices[plan.id] = null;
      }
    });
    setCalculatedPrices(newCalculatedPrices);
  };

  const handleStartPurchase = (planId: number) => {
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      navigate(`/plan-details/${planId}?theme=${theme}&intent=purchase`);
    }
  };

  const handleViewDetails = (planId: number) => {
    navigate(`/plan-details/${planId}?theme=${theme}&intent=details`);
  };

  if (loading) return (
    <div className="homepage" style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <CircularProgress />
    </div>
  );

  if (error) return (
    <div className="homepage" style={{ paddingTop: '100px', padding: '2rem', minHeight: '100vh', backgroundColor: 'var(--background-color)' }}>
      <Alert severity="error">{error}</Alert>
    </div>
  );

  return (
    <div className="homepage" style={{ minHeight: '100vh', paddingBottom: '5rem' }}>
      <div style={{ paddingTop: '100px' }}>
        <section className='plan-section'>
          <h2>پلن‌های ما</h2>
          <div className='plan-grid'>
            {plans.map((plan, index) => {
              let featuresToDisplay = plan.features;
              if (index > 0) {
                const previousPlanFeatures = new Set(plans[index - 1].features);
                featuresToDisplay = plan.features.filter(feature => !previousPlanFeatures.has(feature));
              }

              const planKey = mapPlanIdToPriceKey(plan.id);
              const initialBasePrice = planKey ? NEW_PRICING_BASE_PRICES[planKey] : null;
              const displayPrice = (calculatedPrices[plan.id] !== undefined && calculatedPrices[plan.id] !== null)
                ? calculatedPrices[plan.id]
                : initialBasePrice;

              return (
                <div className='plan-card' key={plan.id}>
                  <div className='plan-card-inner-content'>
                    <h3 style={{ textAlign: 'right' }}>{plan.name}</h3>
                    <div className='plan-price-display'>
                      {displayPrice !== null ? (
                        <>
                          <span className='price-currency'>$</span>
                          <span className='price-amount'>{displayPrice?.toLocaleString('en-US')}</span>
                          <span className='price-period'> / پایه ماهانه</span>
                        </>
                      ) : (
                        <span className='price-placeholder'>قیمت پایه نامشخص</span>
                      )}
                    </div>
                    <ul>
                      {index === 1 && plans[0] && (
                        <li className='plus-separator'>
                          <span>تمام ویژگی‌های پلن "{plans[0].name}"</span>
                          <span className='plus-icon'>+</span>
                        </li>
                      )}

                      {index === 2 && plans[0] && plans[1] && (
                        <li className='plus-separator'>
                          <span>تمام ویژگی‌های پلن‌های "{plans[0].name}" و "{plans[1].name}"</span>
                          <span className='plus-icon'>+</span>
                        </li>
                      )}

                      {featuresToDisplay.map((feature, featureIndex) => (
                        <li key={`${plan.id}-${featureIndex}`}><HiOutlineBadgeCheck /> {feature}</li>
                      ))}
                    </ul>
                    <div className="plan-card-actions">
                      <button onClick={() => handleStartPurchase(plan.id)} className='select-plan-btn buy-btn'>شروع خرید</button>
                      <button onClick={() => handleViewDetails(plan.id)} className='select-plan-btn details-btn'>مشاهده جزئیات</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default PlansPage;